import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import MSwitch from "../common/switch";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/material.css";
import {
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mhome/ui";
import { useRecoilState } from "recoil";
import {
  developerAccessTokenState,
  activeScopeState,
  developerEmailState,
} from "../global-state";
import { Amplify, Auth } from "aws-amplify";
import { DEV_AUTH_CONFIG } from "../aws-config";
import { saveUserSetting } from "../service/user-service";
import { useToast } from "../common/toast";
import InfoIcon from "@mui/icons-material/Info";
import { useWebSocket } from "../ws/websocket";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  useIsSmallScreen,
  useComponentBackgroundColor,
} from "../common/adaptive-theme-provider";
import { useUserInfoManager } from "../views/after-init";

const Developer = () => {
  const history = useHistory();
  const [developerAccessToken, setDeveloperAccessToken] = useRecoilState(
    developerAccessTokenState
  );
  const { sendAndWaitWs } = useWebSocket();
  const [developerEmail, setDeveloperEmail] =
    useRecoilState(developerEmailState);
  const [developerLoggedIn, setDeveloperLoggedIn] = useState(false);
  const path = useLocation();
  const { successToast, warningToast, errorToast } = useToast();
  const isSmallScreen = useIsSmallScreen();

  // Use centralized user info manager
  const { userInfo, fetchUserInfo } = useUserInfoManager();

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  useEffect(() => {
    fetchDeveloperInfo();
  }, []);

  const fetchDeveloperInfo = async () => {
    try {
      Amplify.configure(DEV_AUTH_CONFIG);
      const session = await Auth.currentSession();
      const email = session.getIdToken().payload["cognito:username"];
      setDeveloperAccessToken(session.getAccessToken().getJwtToken());
      setDeveloperEmail(email);
      setDeveloperLoggedIn(true);
    } catch (error) {
      setDeveloperLoggedIn(false);
    }
  };

  const handleScriptModeChange = async (e, v) => {
    const ns = { ...userInfo.setting };
    ns.scriptMode = v;
    await saveSettingChange(ns);
  };

  const handleDebugModeChange = async (e, v) => {
    const ns = { ...userInfo.setting };
    ns.debugMode = v;
    await saveSettingChange(ns);
  };

  const saveSettingChange = async (ns) => {
    const resp = await saveUserSetting(sendAndWaitWs, ns);
    if (resp && !resp.error) {
      successToast("Saved");
      const result = await fetchUserInfo();
      if (result.error) {
        errorToast("Error refreshing user info!");
      }
    } else {
      errorToast("Error!");
    }
  };

  const handleDeveloperLoginLogout = () => {
    if (developerLoggedIn) {
      handleDeveloperLogout();
    } else {
      handleDeveloperLogin();
    }
  };

  const handleDeveloperLogin = async () => {
    const opath = path.pathname + path.search + path.hash;
    Amplify.configure(DEV_AUTH_CONFIG);
    try {
      const oauthUrl = `https://${
        DEV_AUTH_CONFIG.Auth.oauth.domain
      }/login?redirect_uri=${
        DEV_AUTH_CONFIG.Auth.oauth.redirectSignIn
      }&response_type=code&client_id=${
        DEV_AUTH_CONFIG.Auth.userPoolWebClientId
      }&state=${encodeURIComponent(opath)}&scope=openid+email`;
      window.location.href = oauthUrl;
    } catch (error) {
      console.error("Error during sign-in", error);
    }
  };

  const handleDeveloperLogout = async () => {
    try {
      Amplify.configure(DEV_AUTH_CONFIG);
      await Auth.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <div className="flex justify-start items-start flex-col h-full p-4 w-full">
      <div className="static flex items-center justify-start mb-4">
        {isSmallScreen && (
          <>
            <IconButton
              color="inherit"
              edge="start"
              className="mr-2"
              onClick={() => {
                history.replace("/setting");
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <div className="flex">
              <Typography variant="h6" style={{ textAlign: "center" }}>
                Developer
              </Typography>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-2 w-full">
        {developerLoggedIn && (
          <Card
            className="w-full"
            style={{
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
              boxShadow: "none",
            }}
          >
            <CardContent style={{ paddingTop: "16px", paddingBottom: "16px" }}>
              <div className="flex flex-col gap-2">
                <div>
                  <Typography
                    variant="h6"
                    component="div"
                    style={{
                      fontWeight: 500,
                      marginBottom: "4px",
                    }}
                  >
                    Advanced
                  </Typography>
                </div>
                <div className="flex flex-col gap-2">
                  {userInfo?.setting?.engAccount && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Typography variant="body1" component="div">
                          Enable Debug Mode
                        </Typography>
                        <Tooltip title="Enable or disable debug mode.">
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <MSwitch
                        checked={userInfo.setting?.debugMode}
                        onChange={handleDebugModeChange}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Typography variant="body1" component="div">
                        Enable Scripting
                      </Typography>
                      <Tooltip title="Enable or disable scripting mode.">
                        <IconButton>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                    <MSwitch
                      checked={userInfo.setting?.scriptMode}
                      onChange={handleScriptModeChange}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card
          className="w-full"
          style={{
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            boxShadow: "none",
            transition: "all 0.2s ease",
          }}
        >
          <CardContent style={{ paddingTop: "16px", paddingBottom: "20px" }}>
            <div className="flex flex-col gap-2">
              <div>
                <Typography
                  variant="h6"
                  component="div"
                  style={{
                    fontWeight: 500,
                    marginBottom: "4px",
                  }}
                >
                  Developer
                </Typography>
              </div>
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <TextField
                    variant="outlined"
                    value={developerLoggedIn ? developerEmail : "Please login"}
                    disabled
                    fullWidth
                    style={{ height: "40px" }}
                    InputProps={{
                      style: { height: "40px" },
                    }}
                  />
                </div>
                <Button
                  variant="contained"
                  onClick={handleDeveloperLoginLogout}
                  style={{ textTransform: "none" }}
                >
                  {developerLoggedIn ? "Logout" : "Login"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Developer;
